import { collection, deleteDoc, doc, getDoc, getDocs, query, where, runTransaction } from 'firebase/firestore';
import { db } from '../config/firebase'; 
import rolePermissionService from './rolePermissionService';
import permissionService from './permissionService';

const rolesCollection = collection(db, "roles");

const roleService = { 
  getAll: async () => {
    try {  
      const rolesSnapshot = await getDocs(rolesCollection);
      const rolesList = await Promise.all(rolesSnapshot.docs.map(async doc => {
        const roleData = doc.data();
        // Get permission count for each role
        const permissionsCount = await rolePermissionService.getRolePermissionsCount(doc.id);
        return {
          id: doc.id,
          ...roleData,
          permissionsCount
        };
      }));
      return rolesList;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  },

  checkRoleNameExists: async (name, excludeId = null) => {
    try {
      const q = query(
        rolesCollection,
        where("name", "==", name.trim())
      );
      const querySnapshot = await getDocs(q);
      
      // If we're checking for an edit, exclude the current role
      if (excludeId) {
        return querySnapshot.docs.some(doc => doc.id !== excludeId);
      }
      
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking role name:', error);
      throw error;
    }
  },

  add: async (roleData) => {
    try {
      const { permissions, ...roleInfo } = roleData;
      
      // Check if role name already exists
      const nameExists = await roleService.checkRoleNameExists(roleInfo.name);
      if (nameExists) {
        throw new Error('A role with this name already exists');
      }
      
      // Use transaction to ensure atomicity
      return await runTransaction(db, async (transaction) => {
        // First create the role
        const roleRef = doc(collection(db, 'roles'));
        transaction.set(roleRef, roleInfo);
        
        // Then add permissions if any
        if (permissions && permissions.length > 0) {
          for (const permissionName of permissions) {
            const permissionId = await permissionService.getIdByName(permissionName);
            if (permissionId) {
              const rolePermissionRef = doc(collection(db, 'role_permissions'));
              transaction.set(rolePermissionRef, {
                role_id: roleRef.id,
                permission_id: permissionId
              });
            }
          }
        }
        
        return roleRef;
      });
    } catch (error) {
      console.error('Error adding role:', error);
      throw error;
    }
  },

  edit: async ({ id, data }) => {
    try {
      const { permissions, ...roleData } = data;
      
      // Check if role name already exists (excluding current role)
      if (roleData.name) {
        const nameExists = await roleService.checkRoleNameExists(roleData.name, id);
        if (nameExists) {
          throw new Error('A role with this name already exists');
        }
      }
      
      // Use transaction to ensure atomicity
      return await runTransaction(db, async (transaction) => {
        const roleRef = doc(db, 'roles', id);
        
        // Filter out undefined values and create a clean update object
        const cleanRoleData = Object.fromEntries(
          Object.entries(roleData).filter(([_, value]) => value !== undefined)
        );
        
        // Only update if there are fields to update
        if (Object.keys(cleanRoleData).length > 0) {
          transaction.update(roleRef, cleanRoleData);
        }
        
        // Update permissions if provided
        if (permissions) {
          // Delete existing permissions
          const q = query(
            collection(db, 'role_permissions'),
            where("role_id", "==", id)
          );
          const querySnapshot = await getDocs(q);
          querySnapshot.docs.forEach((doc) => {
            transaction.delete(doc.ref);
          });
          
          // Add new permissions
          for (const permissionName of permissions) {
            const permissionId = await permissionService.getIdByName(permissionName);
            if (permissionId) {
              const rolePermissionRef = doc(collection(db, 'role_permissions'));
              transaction.set(rolePermissionRef, {
                role_id: id,
                permission_id: permissionId
              });
            }
          }
        }
      });
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  },

  delete: async (roleId) => {
    try {
      // First delete all role permissions
      await rolePermissionService.deleteRolePermissions(roleId);
      // Then delete the role
      await deleteDoc(doc(db, "roles", roleId));
      return true;
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  },

  getNameById: async (roleId) => {
    if (!roleId) return null;
    try {
      const roleRef = doc(db, 'roles', roleId);
      const roleSnap = await getDoc(roleRef);

      if (roleSnap.exists()) {
        return roleSnap.data().name; 
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting role name:', error);
      throw error;
    }
  },

  getIdByName: async (roleName) => {
    try {
      const rolesSnapshot = await getDocs(rolesCollection);
      const rolesList = rolesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
     
      const formattedRoleName = roleName.trim().toLowerCase().replace(/\s+/g, '');
      const role = rolesList.find(role => role.name.toLowerCase().replace(/\s+/g, '') === formattedRoleName);

      return role ? role.id : null;
    } catch (error) {
      console.error('Error getting role ID:', error);
      throw error;
    }
  }
};

export default roleService;